import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router'
import { RootLayout } from './layouts/root-layout'

import { IndexRedirect } from './lib/auth/index-redirect'
import LoginPage from './pages/login'
const SignupPage = lazy(() => import('./pages/signup'))
const DashboardPage = lazy(() => import('./pages/dashboard'))
const WalletPage = lazy(() => import('./pages/wallet'))
const LeadersPage = lazy(() => import('./pages/leaders'))
const PayoutsPage = lazy(() => import('./pages/payouts'))
const EstimatesPage = lazy(() => import('./pages/estimates'))
const EstimatesQuickPage = lazy(() => import('./pages/estimates-quick'))
const EstimatesFullPage = lazy(() => import('./pages/estimates-full'))
const EstimateDetailPage = lazy(() => import('./pages/estimate-detail'))
const EstimateEditPage = lazy(() => import('./pages/estimate-edit'))
const SettingsPage = lazy(() => import('./pages/settings'))
const ReportsPage = lazy(() => import('./pages/reports'))
const TotalLossPage = lazy(() => import('./pages/total-loss'))
const SchedulingPage = lazy(() => import('./pages/scheduling'))
const CalendarPage = lazy(() => import('./pages/calendar'))
const CustomersPage = lazy(() => import('./pages/customers'))
const CustomerPage = lazy(() => import('./pages/customer'))
const ScheduleEstimatePage = lazy(() => import('./pages/schedule-estimate'))
const IncentivesPage = lazy(() => import('./pages/incentives'))
const OnboardingPage = lazy(() => import('./pages/onboarding'))
const ForgotPasswordPage = lazy(() => import('./pages/forgot-password'))
const ResetPasswordPage = lazy(() => import('./pages/reset-password'))
const SetPasswordPage = lazy(() => import('./pages/set-password'))
const InvitePage = lazy(() => import('./pages/invite'))
const WelcomePasswordPage = lazy(() => import('./pages/welcome-password'))
const WelcomeProfilePage = lazy(() => import('./pages/welcome-profile'))
const WelcomeTourPage = lazy(() => import('./pages/welcome-tour'))
const TeamPage = lazy(() => import('./pages/team'))
const ConsumerTrackPage = lazy(() => import('./pages/consumer-track'))
import { TRACKING_ENABLED } from './components/estimates/consumer-intake-estimate/flags'
import UnauthorizedPage from './pages/unauthorized'
const AdminLoginPage = lazy(() => import('./pages/admin/index'))
const AdminHealthPage = lazy(() => import('./pages/admin/health'))
const AdminUsersPage = lazy(() => import('./pages/admin/users'))
const AdminGhlPage = lazy(() => import('./pages/admin/ghl'))
const AdminActivationManagersPage = lazy(() => import('./pages/admin/activation-managers'))
const AdminActivationContractsPage = lazy(() => import('./pages/admin/activation-contracts'))
const ActivatePage = lazy(() => import('./pages/activate'))
const CallsPage = lazy(() => import('./pages/calls'))
const DeskPage = lazy(() => import('./pages/desk'))
const CallDetailPage = lazy(() => import('./pages/call-detail'))
const QuickEstimatePlaceholderPage = lazy(() => import('./pages/quick-estimate-placeholder'))
const IvrRedirectPage = lazy(() => import('./pages/ivr-redirect'))

// Every page but login is its own chunk (one Suspense around the routes):
// the first load of any page, the consumer one included, no longer downloads
// the whole staff app.
// The consumer intake flow (the smart-camera photo walk) is split out of the
// main bundle: only /page/:slug and the staff demo page load it, so the rest
// of the app never downloads the camera's code.
const ConsumerFlowPage = lazy(() => import('./pages/consumer-flow'))
const ConsumerIntakeEstimatePage = lazy(() => import('./pages/consumer-intake-estimate'))

function ConsumerFallback() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-zinc-50 text-sm text-zinc-500">
      Loading…
    </div>
  )
}

export function App() {
  return (
    <RootLayout>
      <Suspense fallback={<ConsumerFallback />}>
      <Routes>
        <Route path="/" element={<IndexRedirect />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/invites/:uuid" element={<InvitePage />} />
        <Route path="/welcome/password" element={<WelcomePasswordPage />} />
        <Route path="/welcome/profile" element={<WelcomeProfilePage />} />
        <Route path="/welcome/role-tour" element={<WelcomeTourPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/estimates" element={<EstimatesPage />} />
        <Route path="/estimates/quick" element={<EstimatesQuickPage />} />
        <Route path="/estimates/full" element={<EstimatesFullPage />} />
        <Route
          path="/estimates/consumer-intake"
          element={
            <Suspense fallback={<ConsumerFallback />}>
              <ConsumerIntakeEstimatePage />
            </Suspense>
          }
        />
        <Route path="/estimates/:id" element={<EstimateDetailPage />} />
        <Route path="/estimates/:id/edit" element={<EstimateEditPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/account" element={<SettingsPage />} />
        <Route path="/settings/:kind/:teamId/:section" element={<SettingsPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/wallet" element={<WalletPage />} />
        <Route path="/leaders" element={<LeadersPage />} />
        <Route path="/payouts" element={<PayoutsPage />} />
        <Route path="/total-loss" element={<TotalLossPage />} />
        <Route path="/scheduling" element={<SchedulingPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/customers/:id" element={<CustomerPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/schedule/:estimateUuid" element={<ScheduleEstimatePage />} />
        <Route path="/incentives" element={<IncentivesPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/team" element={<TeamPage />} />
        {/* Public consumer surface — no auth, no AppLayout. */}
        <Route
          path="/page/:bodyshopSlug"
          element={
            <Suspense fallback={<ConsumerFallback />}>
              <ConsumerFlowPage />
            </Suspense>
          }
        />
        {TRACKING_ENABLED && <Route path="/track/:requestUuid" element={<ConsumerTrackPage />} />}
        {/* Public activation — the body-shop owner clicking the link we
            emailed them. No account, no login: the account is created later,
            on the onboarding call. */}
        <Route path="/activate/:uuid" element={<ActivatePage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="/admin" element={<AdminLoginPage />} />
        <Route path="/admin/health" element={<AdminHealthPage />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/ghl" element={<AdminGhlPage />} />
        <Route path="/admin/activation/managers" element={<AdminActivationManagersPage />} />
        <Route path="/admin/activation/contracts" element={<AdminActivationContractsPage />} />
        {/* The Desk: Tasks · Inbox · Customers · Estimates · Results. */}
        <Route path="/desk" element={<DeskPage section="tasks" />} />
        <Route path="/desk/inbox" element={<DeskPage section="inbox" />} />
        <Route path="/desk/customers" element={<DeskPage section="customers" />} />
        <Route path="/desk/customers/:customerId" element={<DeskPage section="customer" />} />
        <Route path="/desk/estimates" element={<DeskPage section="estimates" />} />
        <Route path="/desk/results" element={<DeskPage section="results" />} />
        <Route path="/calls" element={<CallsPage />} />
        <Route path="/calls/:id" element={<CallDetailPage />} />
        <Route path="/quick-estimate/placeholder" element={<QuickEstimatePlaceholderPage />} />
        <Route path="/r/:token" element={<IvrRedirectPage />} />
      </Routes>
      </Suspense>
    </RootLayout>
  )
}
